import { Instance, SnapshotOut, types } from 'mobx-state-tree'
import { Platform } from 'react-native'
import { identifyPush, isPushGranted, setPushAttributes, syncPushToken } from '@/modules/push'

import { UserModelType } from '../api-store'
import { DdLogs, datadogSetAttributes, removeUserDataDog, setUserDataDog } from '../../../module/datadog'
import { withRootStore } from '../extensions'

import { initRemoteConfig } from '../../../module/remote-config/useInitRemoteConfig'
import { getEnvStage } from '../root.store'
import { setRemoteConfigUserContext } from '../../../module/remote-config/useRemoteConfig'
import { segmentClient } from '@/models/analytics/segment'
import { shouldAliasToAuth0, toAnalyticsIdentity } from '@/models/analytics/analytics-identity'
/**
 * A TelemetryStore model.
 */

const isWeb = Platform.OS === 'web'

export interface DatadogUser extends UserModelType {
  gameId?: string
}
export const TelemetryStore = types
  .model('TelemetryStore')
  .props({})
  .extend(withRootStore)
  .actions((self) => ({
    async setUserAnalytics(user: DatadogUser) {
      // session.user may not be set yet (fetchMe calls setUser first), so resolve identity from `user`
      const { userId, traits } = toAnalyticsIdentity(user)
      self.rootStore.analytics.setUserIdentify(userId, {
        name: user.name,
        email: user.email || undefined,
        anonymousId:
          self.rootStore.share.segmentAnonymousId !== '' ? self.rootStore.share.segmentAnonymousId : undefined,
        ...traits
      })
    }
  }))
  .actions((self) => ({
    async setGameAttributes(...attributes: Parameters<typeof datadogSetAttributes>) {
      datadogSetAttributes(...attributes)
      // DdLogs.info(`telemetry store setPlayers ${JSON.stringify(attributes)}`)
    },
    async setUser(user: DatadogUser) {
      try {
        const role = (user.userRoles ?? []).join(',')
        const email = user.email ?? ''
        const userId = user?._id

        await self.rootStore.memberships.getMySubscription()

        const userType = self.rootStore.memberships.subscription?.type ?? 'free'
        if (shouldAliasToAuth0(user.auth0Id, self.rootStore.analytics.getSegmentUserID())) {
          segmentClient.alias(user.auth0Id)
        }
        self.setUserAnalytics(user)

        const { stage } = await getEnvStage()

        await setRemoteConfigUserContext(
          {
            analyticsUserProperties: {
              id: userId,
              user_type: userType,
              user_privileges: role,
              email,
              phoneNumber: user.phoneNumber || '',
              createdAt: new Date(user.createdAt).getTime().toString(),
              platform: Platform.OS,
              stage: stage
            }
          },
          user.auth0Id
        )

        await initRemoteConfig()

        if (user.auth0Id) {
          // auth0Id is the canonical external id. Auth0 is the identity source of
          // truth, and it's what Customer.io keys profiles on — customerio.provider.ts
          // passes identity.auth0Id straight to CustomerIO.identify({ userId }).
          await identifyPush({
            auth0Id: user.auth0Id,
            legacyUserId: userId,
            email
          })
        }

        // https://documentation.onesignal.com/docs/add-user-data-tags
        await setPushAttributes({
          user_type: userType,
          user_privileges: role,
          user_name: user.name || undefined,
          user_created_at: user.createdAt ? Math.round(new Date(user.createdAt).getTime() / 1000).toString() : undefined
        })

        if (!isWeb && (await isPushGranted())) {
          await syncPushToken()
        }

        await setUserDataDog({
          userId,
          user,
          role
        })
      } catch (error) {
        console.error(`telemetry-store setUser ${error}`)
        DdLogs.error(`telemetry-store setUser ${error}`)
      }
    },
    async removeUser() {
      await removeUserDataDog()
    },
    /** Drops the game id from the Datadog user without re-running the full identify flow. */
    async clearUserGameId(user?: UserModelType) {
      if (!user?._id) return
      try {
        await setUserDataDog({
          userId: user._id,
          user,
          role: (user.userRoles ?? []).join(',')
        })
      } catch (error) {
        DdLogs.error(`telemetry-store clearUserGameId ${error}`)
      }
    },
    async setUserSubscription(subscriptionType: string) {
      // TODO: implement
      // DdSdkReactNative.addUserExtraInfo({
      //   subscription: subscriptionType
      // })
    },
    async setUserCurrentGameId(gameId: string) {
      // TODO: implement
      // DdSdkReactNative.addUserExtraInfo({
      //   gameId
      // })
    }
  }))

/**
 * The TelemetryStore instance.
 */
export type TelemetryStoreType = Instance<typeof TelemetryStore>

/**
 * The data of a TelemetryStore.
 */
export type TelemetryStoreSnapshot = SnapshotOut<typeof TelemetryStore>
